import React, { FC, useCallback, useEffect, useRef } from "react";
import Swecomap from "./components/Swecomap";
import swecomapUtils from "../../utils/swecomap";
import mapUtils from "../../utils/map";
import GeomSelect from "./components/GeomSelect";
import { useMap } from "../../App";

import ol from "openlayers";
import Spinner from "../common/F7/Spinner";
import { useInventoryContents } from "../../hooks/database/useInventoryContents";
import { useCurrentProjectConfig } from "../../hooks/database/useCurrentProjectConfig";
import { useCurrentInventory } from "../../hooks/database/useCurrentInventory";
import { useCurrentInventoryId } from "../../hooks/url";
import _ from "lodash";
import { useLocation } from "react-router-dom";

const Map: FC = () => {
  const map = useMap();

  const { inventoryId } = useCurrentInventoryId();
  const { data: inventory } = useCurrentInventory();
  const { data: objects } = useInventoryContents();

  const pointSourceRef = useRef(new ol.source.Vector());
  const lineSourceRef = useRef(new ol.source.Vector());
  const polygonSourceRef = useRef(new ol.source.Vector());

  const addFeatures = useCallback(() => {
    if (!map || !objects) {
      return;
    }

    const geojsonFormat = new ol.format.GeoJSON();
    const features = objects.map((row) => {
      const geometry = geojsonFormat.readGeometry(row.geom.geometry, {
        dataProjection: "EPSG:4326",
        // TODO (on openlayers upgrade): it should work to just pass in the projection, might be a bug
        featureProjection: map.getView().getProjection().getCode(),
      });

      const feature = new ol.Feature({
        geometry: geometry,
        title: row.title,
        formdata: row.data,
      });
      feature.setId(row.id);
      return feature;
    });

    // add or replace features
    for (const feature of features) {
      const type = feature.getGeometry().getType();
      const source = type === "Point" ? pointSourceRef.current : type === "LineString" ? lineSourceRef.current : polygonSourceRef.current;
      const existingFeature = source.getFeatureById(feature.getId());
      if (existingFeature) {
        existingFeature.setProperties(feature.getProperties());
      } else {
        source.addFeature(feature);
      }
    }
    // remove features
    const ids = features.map((f) => f.getId());
    for (const source of [pointSourceRef.current, lineSourceRef.current, polygonSourceRef.current]) {
      source
        .getFeatures()
        .filter((f) => !ids.includes(f.getId()))
        .forEach((f) => source.removeFeature(f));
    }
  }, [map, objects]);

  const haveLoadedInventoryOnceRef = useRef<string | undefined>(undefined);

  const { data: config } = useCurrentProjectConfig();
  useEffect(() => {
    const styles = inventory?.styles || {};
    const cluster = inventory?.cluster || false;

    if (!map) {
      return;
    }
    mapUtils.getInventoryLayers(map).forEach((inventoryLayer) => {
      map.removeLayer(inventoryLayer);
    });

    map.addLayer(
      // @ts-ignore
      // TODO: reintroduce animated cluster
      // new window.ol.layer.AnimatedCluster({
      new ol.layer.Vector({
        name: "Inventory",
        source: new ol.source.Cluster({
          distance: cluster ? 40 : 0,
          source: pointSourceRef.current,
        }),
        animationDuration: 700,
        style: mapUtils.getStyle(styles, "Point"),
        geomType: "Point",
      } as olx.layer.VectorOptions)
    );
    map.addLayer(
      new ol.layer.Vector({
        name: "InventoryPolyline",
        source: lineSourceRef.current,
        style: mapUtils.getStyle(styles, "LineString"),
      } as olx.layer.VectorOptions)
    );
    map.addLayer(
      new ol.layer.Vector({
        name: "InventoryPolygon",
        source: polygonSourceRef.current,
        style: mapUtils.getStyle(styles, "Polygon"),
      } as olx.layer.VectorOptions)
    );

    if (objects) {
      addFeatures();

      if (
        objects!.length > 0 &&
        // only zoom-to-extent if this is the first time loading the inventory or if we're on the default
        // center (usually only happens in a hot-reloading scenario, but annoying during development)
        (haveLoadedInventoryOnceRef.current !== inventoryId || _.isEqual(config?.config?.view.center, map.getView().getCenter()))
      ) {
        const extent = mapUtils.getExtentFromSources([pointSourceRef.current, lineSourceRef.current, polygonSourceRef.current]);
        mapUtils.zoomToExtent(map, extent);
      }

      haveLoadedInventoryOnceRef.current = inventoryId;
    }
  }, [map, addFeatures, inventory?.styles, inventory?.cluster, objects, inventoryId, config?.config?.view.center]);

  const swecomapOnLoad = useCallback(() => {
    if (sessionStorage.getItem("showInfoButton") && sessionStorage.getItem("showInfoButton") === "true") {
      swecomapUtils.enableAutoselect();
    } else {
      swecomapUtils.disableAutoselect();
    }
    swecomapUtils.disableDoubleClickZoom();

    //Note-to-self: Don't unlock rotation before the draw extent from buffered pixels is fixed
    swecomapUtils.disableRotate();
  }, []);

  const { data } = useCurrentProjectConfig();

  const location = useLocation();
  const pageWithMap =
    (location.pathname.includes("/i/inventory") && !location.pathname.includes("/o/") && !location.pathname.endsWith("/edit")) ||
    location.pathname.endsWith("/o/new") ||
    location.pathname.endsWith("/modify") ||
    location.pathname.includes("/offline-maps/new");

  if (!data?.config || !inventory || !objects) {
    return <Spinner />;
  }

  return (
    <>
      <GeomSelect />
      <Swecomap config={data.config} onLoaded={swecomapOnLoad} visible={pageWithMap} />
    </>
  );
};

export default Map;
